import { Params, Path, RoutesType } from '@/entities/routes';
import { queryBuild } from '@/utils/queryBuild';
import { stringifyObject } from '@/utils/stringfyObject';

class Routes<T extends RoutesType> {
  public children: T;

  public memoizedValues: Record<string, string>;

  constructor(children: T) {
    this.children = children;
    this.memoizedValues = {};
  }

  private getMemoValue(key: string, params: Params = {}): string | undefined {
    let valueKey = key;

    if (params.path) {
      valueKey += `:${stringifyObject(params.path)}`;
    }

    if (params.query) {
      valueKey += `--${stringifyObject(params.query)}`;
    }

    return this.memoizedValues[valueKey];
  }

  private setMemoValue(value: string, key: string, params: Params = {}) {
    let valueKey = key;

    if (params.path) {
      valueKey += `:${stringifyObject(params.path)}`;
    }

    if (params.query) {
      valueKey += `--${stringifyObject(params.query)}`;
    }

    this.memoizedValues[valueKey] = value;
  }

  public getLink<P extends Path<T>>(key: P, params?: Params) {
    const memoizedValue = this.getMemoValue(String(key), params);

    if (memoizedValue) {
      return memoizedValue;
    }

    const keys = String(key).split(`.`);

    let { link } = keys.reduce(
      (accumulator, currentValue) => {
        let newLink = accumulator.link;

        if (accumulator.previousChildren) {
          newLink += accumulator.previousChildren[currentValue].url
            .split(`/`)
            .map((path) => {
              if (path.startsWith(`:`)) {
                if (params?.path) {
                  return params.path[path.substring(1)];
                }

                throw new Error(`param ${path.substring(1)} is required on ${String(key)}`);
              }

              return path;
            })
            .join(`/`);
        }

        return {
          link: newLink,
          previousChildren: accumulator.previousChildren
            ? accumulator.previousChildren[currentValue].children
            : accumulator.previousChildren,
        };
      },
      {
        link: ``,
        previousChildren: this.children as RoutesType | undefined,
      },
    );

    if (params?.query) {
      link += queryBuild(params.query);
    }

    this.setMemoValue(link, String(key), params);

    return link;
  }
}

export const ROUTES = new Routes({
  home: {
    base: `/`,
    url: `/`,
  },
  admin: {
    base: `admin`,
    url: `/admin`,
    children: {
      company: {
        base: `company/*`,
        url: `/company`,
        children: {
          list: {
            base: ``,
            url: ``,
          },
          create: {
            base: `create`,
            url: `/create`,
          },
          open: {
            base: `:id`,
            url: `/:id`,
            children: {
              users: {
                base: `users`,
                url: `/users`,
                children: {
                  root: {
                    base: ``,
                    url: ``,
                  },
                  edit: {
                    base: `:userId/edit`,
                    url: `/:userId/edit`,
                  },
                },
              },
              roles: {
                base: `roles`,
                url: `/roles`,
              },
              createRole: {
                base: `roles/create`,
                url: `/roles/create`,
              },
              editRole: {
                base: `roles/:roleId/edit`,
                url: `/roles/:roleId/edit`,
              },
              tools: {
                base: `tools`,
                url: `/tools`,
              },
              generalInfo: {
                base: `general-info`,
                url: `/general-info`,
              },
              security: {
                base: `security`,
                url: `/security`,
              },
              finances: {
                base: `finances`,
                url: `/finances`,
              },
            },
          },
        },
      },
      users: {
        base: `users/*`,
        url: `/users`,
        children: {
          list: {
            base: ``,
            url: ``,
          },
          create: {
            base: `create`,
            url: `/create`,
          },
          open: {
            base: `:id`,
            url: `/:id`,
            children: {
              edit: {
                base: `edit`,
                url: `/edit`,
              },
            },
          },
        },
      },
      status: {
        base: `status/*`,
        url: `/status`,
        children: {
          root: {
            base: ``,
            url: ``,
          },
        },
      },
      ai: {
        base: `ai/*`,
        url: `/ai`,
        children: {
          dashboard: {
            base: ``,
            url: ``,
          },
          translation: {
            base: `translation`,
            url: `/translation`,
          },
          settings: {
            base: `settings`,
            url: `/settings`,
          },
        },
      },
      profile: {
        base: `profile/*`,
        url: `/profile`,
        children: {
          home: {
            base: ``,
            url: ``,
          },
        },
      },
      agenda: {
        base: `agenda/*`,
        url: `/agenda`,
        children: {
          list: {
            base: ``,
            url: ``,
          },
          calendar: {
            base: `calendar`,
            url: `/calendar`,
          },
          events: {
            base: `events`,
            url: `/events`,
            children: {
              list: { base: ``, url: `` },
              create: { base: `create`, url: `/create` },
              edit: { base: `:id/edit`, url: `/:id/edit` },
            },
          },
        },
      },
      workr: {
        base: `workr/*`,
        url: `/workr`,
        children: {
          dashboard: {
            base: ``,
            url: ``,
          },
        },
      },
    },
  },
  cms: {
    base: `cms/*`,
    url: `/cms`,
  },
  irm: {
    base: `irm/*`,
    url: `/irm`,
    children: {
      dashboard: {
        base: ``,
        url: ``,
      },
      monitoring: {
        base: `monitoring`,
        url: `/monitoring`,
        children: {
          home: {
            base: ``,
            url: ``,
          },
        },
      },
      shareholders: {
        base: `shareholders`,
        url: `/shareholders`,
        children: {
          home: {
            base: ``,
            url: ``,
          },
        },
      },
      contacts: {
        base: `contacts`,
        url: `/contacts`,
        children: {
          home: {
            base: ``,
            url: ``,
          },
        },
      },
      shareholderHistory: {
        base: `shareholder-history/:id`,
        url: `/shareholder-history/:id`,
      },
      groupHistory: {
        base: `group-history/:id`,
        url: `/group-history/:id`,
      },
      shareholderBase: {
        base: `shareholder-base`,
        url: `/shareholder-base`,
      },
      importStatus: {
        base: `import-status`,
        url: `/import-status`,
      },
      events: {
        base: `events`,
        url: `/events`,
      },
      agglutinationDetails: {
        base: `agglutination-details`,
        url: `/agglutination-details`,
      },
      videoconference: {
        base: `videoconference/*`,
        url: `/videoconference`,
        children: {
          rooms: {
            base: `rooms`,
            url: `/rooms`,
            children: {
              edit: {
                base: `:id/edit`,
                url: `/:id/edit`,
              },
              createAttendee: {
                base: `:id/create-attendee`,
                url: `/:id/create-attendee`,
              },
              editAttendee: {
                base: `:id/edit-attendee`,
                url: `/:id/edit-attendee`,
              },
            },
          },
          config: {
            base: `config`,
            url: `/config`,
          },
        },
      },
      services: {
        base: `demais-servicos/*`,
        url: `/demais-servicos`,
        children: {
          home: {
            base: ``,
            url: ``,
          },
          catalogo: {
            base: `catalogo`,
            url: `/catalogo`,
          },
        },
      },
      astrid: {
        base: `astrid/*`,
        url: `/astrid`,
        children: {
          home: {
            base: ``,
            url: ``,
          },
          conversations: {
            base: `conversas`,
            url: `/conversas`,
          },
          integrations: {
            base: `integracoes`,
            url: `/integracoes`,
          },
        },
      },
      config: {
        base: `config`,
        url: `/config`,
        children: {
          root: {
            base: ``,
            url: ``,
          },
          shareholderBase: {
            base: `shareholder-base`,
            url: `/shareholder-base`,
            children: {
              files: {
                base: `files`,
                url: `/files`,
              },
              shareExtraction: {
                base: `share-extraction`,
                url: `/share-extraction`,
              },
            },
          },
          sentEmails: {
            base: `sent-emails`,
            url: `/sent-emails`,
            children: {
              manager: {
                base: `manager`,
                url: `/manager`,
              },
            },
          },
          reports: {
            base: `reports`,
            url: `/reports`,
            children: {
              managerGroups: {
                base: `manager-groups`,
                url: `/manager-groups`,
              },
            },
          },
        },
      },
    },
  },
  webcast: {
    base: `webcast/*`,
    url: `/webcast`,
  },
  ai: {
    base: `inteligencia-artificial/*`,
    url: `/inteligencia-artificial`,
  },
  profile: {
    base: `profile/*`,
    url: `/profile`,
    children: {
      home: {
        base: ``,
        url: ``,
      },
      irmConfig: {
        base: `irm-config`,
        url: `/irm-config`,
        children: {
          root: {
            base: ``,
            url: ``,
          },
          shareholderBase: {
            base: `shareholder-base`,
            url: `/shareholder-base`,
            children: {
              shareExtraction: {
                base: `share-extraction`,
                url: `/share-extraction`,
              },
            },
          },
          sentEmails: {
            base: `sent-emails`,
            url: `/sent-emails`,
            children: {
              manager: {
                base: `manager`,
                url: `/manager`,
              },
            },
          },
          reports: {
            base: `reports`,
            url: `/reports`,
            children: {
              managerGroups: {
                base: `manager-groups`,
                url: `/manager-groups`,
              },
            },
          },
        },
      },
    },
  },
  email: {
    base: `email/*`,
    url: `/email`,
    children: {
      dashboard: {
        base: ``,
        url: ``,
      },
    },
  },

  services: {
    base: `demais-servicos/*`,
    url: `/demais-servicos`,
    children: {
      home: {
        base: ``,
        url: ``,
      },
      catalogo: {
        base: `catalogo`,
        url: `/catalogo`,
      },
    },
  },

  automation: {
    base: `automation/*`,
    url: `/automation`,
    children: {
      home: {
        base: ``,
        url: ``,
      },
      documents: {
        base: `documents/*`,
        url: `/documents`,
        children: {
          upload: {
            base: `upload`,
            url: `/upload`,
          },
          settings: {
            base: `settings`,
            url: `/settings`,
          },
        },
      },
    },
  },
  calendar: {
    base: `calendar/*`,
    url: `/calendar`,
    children: {
      home: {
        base: ``,
        url: ``,
      },
    },
  },
} as const);
